/**
 * BlockchainTrustSection — section Home dédiée à la traçabilité Blockchain.
 * Mise en avant du badge "Vérifié sur Blockchain" + lien vers une page
 * publique de démonstration.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Hash, Globe2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { publicTraceUrl } from '../utils/publicUrl';

const BENEFITS = [
  { icon: Hash, title: 'Empreinte SHA-256 immuable', desc: 'Chaque lot Premium reçoit une signature cryptographique unique, infalsifiable.' },
  { icon: Globe2, title: 'Vérifiable par tout acheteur', desc: 'Un simple scan QR ouvre la fiche publique : producteurs, parcelle, qualité.' },
  { icon: CheckCircle2, title: 'EUDR-ready', desc: 'Preuve d\'origine compatible Règlement UE 2023/1115 sur la déforestation.' },
];

const BlockchainTrustSection = () => {
  // Lot de démo : si aucun lot disponible, le lien tombera sur 404 gracieux
  const demoLotCode = 'GL-2026-004-GALEBRE';

  return (
    <section
      className="relative bg-gradient-to-br from-[#0F2818] via-[#1A3622] to-[#0F2818] py-16 md:py-24 overflow-hidden"
      data-testid="blockchain-trust-section"
    >
      {/* Subtle grain / decorative dots */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
        backgroundSize: '32px 32px',
      }} />

      <div className="relative max-w-6xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* LEFT : pitch + benefits */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/30 rounded-full px-3 py-1.5 mb-5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />
              <span className="text-[11px] font-bold tracking-wider uppercase text-emerald-200">
                Nouveau · Traçabilité Blockchain
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Chaque lot Premium,<br />
              <span className="bg-gradient-to-r from-emerald-300 to-[#d4a574] bg-clip-text text-transparent">
                vérifié sur Blockchain.
              </span>
            </h2>

            <p className="text-base md:text-lg text-white/75 leading-relaxed mb-8 max-w-xl">
              Une preuve cryptographique infalsifiable est rattachée à chaque lot de cacao. Vos acheteurs scannent le QR, et accèdent à une fiche publique authentifiée — argument décisif face aux exigences EUDR.
            </p>

            <ul className="space-y-4 mb-8">
              {BENEFITS.map((b) => (
                <li key={b.title} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                    <b.icon className="h-4 w-4 text-emerald-300" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{b.title}</p>
                    <p className="text-xs text-white/65 leading-relaxed mt-0.5">{b.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <Link
                to={`/verify-lot/${demoLotCode}`}
                className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-500 to-emerald-700 text-white rounded-md text-sm font-semibold hover:opacity-95 shadow-lg shadow-emerald-900/40 transition-all"
                data-testid="blockchain-demo-link"
              >
                <ShieldCheck className="h-4 w-4" />
                Voir une preuve en direct
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pour-les-cooperatives"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white/10 text-white border border-white/20 rounded-md text-sm font-semibold hover:bg-white/15 transition-colors"
              >
                Activer pour ma coopérative
              </Link>
            </div>
          </div>

          {/* RIGHT : mock visual badge card */}
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-br from-emerald-400/20 via-transparent to-[#d4a574]/15 rounded-3xl blur-2xl pointer-events-none" />
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-white/30">
              <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-900 text-white px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 border border-white/30 flex items-center justify-center relative">
                    <ShieldCheck className="h-5 w-5 text-white" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-emerald-700 animate-pulse" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest opacity-80">Statut</p>
                    <h3 className="text-sm font-bold tracking-wide">Vérifié sur Blockchain</h3>
                  </div>
                </div>
                <span className="text-[10px] bg-white/20 border border-white/40 rounded-full px-2.5 py-1 font-bold tracking-wider">
                  AUTHENTIQUE
                </span>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] mb-1 flex items-center gap-1">
                    <Hash className="h-3 w-3" /> Empreinte SHA-256
                  </p>
                  <code className="block text-[9px] font-mono text-[#1A3622] bg-emerald-50 border border-emerald-100 rounded px-2 py-2 break-all">
                    8d39ed1d5e0c25c967575c4d13d6836b5aa6e298a061ceb5cf666759f6f632b7
                  </code>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div className="bg-[#F9FAFB] border border-[#E5E5E0] rounded px-2.5 py-1.5">
                    <p className="text-[#9CA3AF] uppercase tracking-wide">Lot</p>
                    <p className="font-mono font-bold text-[#1A3622] mt-0.5">GL-2026-004</p>
                  </div>
                  <div className="bg-[#F9FAFB] border border-[#E5E5E0] rounded px-2.5 py-1.5">
                    <p className="text-[#9CA3AF] uppercase tracking-wide">Poids</p>
                    <p className="font-bold text-[#1A3622] mt-0.5">750 kg</p>
                  </div>
                  <div className="bg-[#F9FAFB] border border-[#E5E5E0] rounded px-2.5 py-1.5">
                    <p className="text-[#9CA3AF] uppercase tracking-wide">Récolte</p>
                    <p className="font-bold text-[#1A3622] mt-0.5">23 mai 2026</p>
                  </div>
                  <div className="bg-[#F9FAFB] border border-[#E5E5E0] rounded px-2.5 py-1.5">
                    <p className="text-[#9CA3AF] uppercase tracking-wide">Village</p>
                    <p className="font-bold text-[#1A3622] mt-0.5">Galébré</p>
                  </div>
                </div>
                <p className="text-[10px] text-[#6B7280] italic pt-1">
                  Cliquez sur "Voir une preuve en direct" pour ouvrir la fiche publique scannée par les acheteurs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlockchainTrustSection;
